// auth.ts - 修复版本，移除所有mock_token格式

import {ApiClient, endpoints} from './request'
import type {SingleResponse} from './request'
import {logger, config} from '../config'
import { generateTokenPair } from '../utils/jwtUtils'

// 认证相关的类型定义（保持不变）
export interface ThirdPartyLoginCmd {
    thirdPartyUserId: string
    timestamp: number
    nonce: string
    signature: string
}

export interface LoginCmd {
    username: string
    password: string
    type: string
    loginPartner?: {
        partnerUserId: string
        timestamp: number
        nonce: string
    }
}

export interface RefreshTokenCmd {
    refreshToken: string
}

export interface LoginResponse {
    accessToken: string
    refreshToken: string
    expiresIn: number
}

export interface RefreshTokenResponse {
    accessToken: string
    refreshToken: string
    expireTime: number
}

/**
 * 从URL参数或iframe通信中获取第三方登录认证信息
 */
export function getThirdPartyAuthParams(): Partial<ThirdPartyLoginCmd> {
    const params: Partial<ThirdPartyLoginCmd> = {
        signature:"qbs4efVz02m5ox7ZzHOJDC0Rhiib4uP8EUrBguDqeLA=",
        thirdPartyUserId:"111111",
        nonce:"111111",
        timestamp:1754586002000
    }

    const urlParams = new URLSearchParams(window.location.search)
    urlParams.forEach((value, key) => {
        if (key === 'thirdPartyUserId') params.thirdPartyUserId = value
        if (key === 'nonce') params.nonce = value
        if (key === 'timestamp') params.timestamp = parseInt(value, 10)
        if (key === 'signature') params.signature = value
    })

    return params
}

/**
 * 从URL参数获取普通登录信息
 */
export function getLoginParams(): Partial<LoginCmd> {
    const params: Partial<LoginCmd> = {}

    const urlParams = new URLSearchParams(window.location.search)
    urlParams.forEach((value, key) => {
        if (key === 'username') params.username = value
        if (key === 'password') params.password = value
        if (key === 'type') params.type = value
        if (key === 'partnerUserId') {
            params.loginPartner = {
                ...params.loginPartner,
                partnerUserId: value
            } as any
        }
        if (key === 'partnerTimestamp') {
            params.loginPartner = {
                ...params.loginPartner,
                timestamp: parseInt(value, 10)
            } as any
        }
        if (key === 'partnerNonce') {
            params.loginPartner = {
                ...params.loginPartner,
                nonce: value
            } as any
        }
    })

    return params
}

/**
 * 第三方登录验证
 */
export async function validateThirdPartyLogin(loginCmd: ThirdPartyLoginCmd): Promise<LoginResponse> {
    try {
        const response: SingleResponse<LoginResponse> = await ApiClient.post(
            endpoints.auth.thirdPartyLogin,
            loginCmd,
            {skipAuth: true}
        )

        if (response.success && response.data) {
            await saveAuthData(response.data)
            return response.data
        }

        throw new Error(response.errMsg || '第三方登录失败')

    } catch (error) {
        logger.error('第三方登录验证失败:', error)
        throw error
    }
}

/**
 * 普通登录验证
 */
export async function validateLogin(loginCmd: LoginCmd): Promise<LoginResponse> {
    try {
        logger.log('正在验证普通登录...', {
            username: loginCmd.username,
            type: loginCmd.type,
            hasPartner: !!loginCmd.loginPartner
        })

        const response: SingleResponse<LoginResponse> = await ApiClient.post(
            endpoints.auth.login,
            loginCmd,
            {skipAuth: true}
        )

        if (response.success && response.data) {
            await saveAuthData(response.data)
            logger.log('普通登录成功')
            return response.data
        }

        throw new Error(response.errMsg || '登录失败')

    } catch (error) {
        logger.error('普通登录验证失败:', error)

        throw error
    }
}

/**
 * 保存认证数据
 */
async function saveAuthData(loginData: LoginResponse): Promise<void> {
    const tokenInfo = {
        accessToken: loginData.accessToken,
        refreshToken: loginData.refreshToken,
        expiresIn: loginData.expiresIn,
        loginTime: Date.now()
    }

    localStorage.setItem('tokenInfo', JSON.stringify(tokenInfo))
    localStorage.setItem(config.auth.accessTokenKey, loginData.accessToken)
    localStorage.setItem(config.auth.refreshTokenKey, loginData.refreshToken)
}

/**
 * 获取JWT格式登录响应（开发环境）- 替换原来的getMockLoginResponse
 */
async function getJWTLoginResponse(userId: string): Promise<LoginResponse> {
    logger.log('开发环境: 生成JWT格式认证数据')

    // 生成用户信息
    const userInfo = {
        username: userId.includes('mock') || userId.includes('dev') ? '测试用户' : userId,
        balance: 1688.88,
        vipLevel: 'VIP 3'
    }

    // 生成JWT格式的token对
    const tokenPair = generateTokenPair(userId, userInfo)

    const tokenInfo = {
        accessToken: tokenPair.accessToken,
        refreshToken: tokenPair.refreshToken,
        expiresIn: tokenPair.expiresIn,
        loginTime: Date.now()
    }

    // 保存认证数据
    localStorage.setItem('tokenInfo', JSON.stringify(tokenInfo))
    localStorage.setItem(config.auth.accessTokenKey, tokenPair.accessToken)
    localStorage.setItem(config.auth.refreshTokenKey, tokenPair.refreshToken)

    // 保存用户信息
    const mockUserInfo = {
        userId: userId,
        username: userInfo.username,
        balance: userInfo.balance,
        vipLevel: userInfo.vipLevel
    }
    localStorage.setItem(config.auth.userInfoKey, JSON.stringify(mockUserInfo))

    // 调试信息
    if (config.dev.debugMode) {
        logger.debug('生成的JWT Token信息:', {
            accessToken: tokenPair.accessToken.substring(0, 50) + '...',
            refreshToken: tokenPair.refreshToken.substring(0, 50) + '...',
            isValidJWT: tokenPair.accessToken.split('.').length === 3,
            userInfo: mockUserInfo
        })
    }

    return {
        accessToken: tokenPair.accessToken,
        refreshToken: tokenPair.refreshToken,
        expiresIn: tokenPair.expiresIn
    }
}

/**
 * 智能登录 - 自动判断登录方式
 */
export async function smartLogin(): Promise<LoginResponse> {

    // 1. 尝试第三方登录
    const thirdPartyParams = getThirdPartyAuthParams()
    if (thirdPartyParams.thirdPartyUserId &&
        thirdPartyParams.nonce &&
        thirdPartyParams.timestamp &&
        thirdPartyParams.signature) {

        return await validateThirdPartyLogin(thirdPartyParams as ThirdPartyLoginCmd)
    }

    // 2. 尝试普通登录
    const loginParams = getLoginParams()
    if (loginParams.username && loginParams.password && loginParams.type) {
        logger.log('检测到普通登录参数，使用普通登录')
        return await validateLogin(loginParams as LoginCmd)
    }

    // 3. 尝试从iframe消息获取认证信息
    try {
        logger.log('尝试从iframe消息获取认证信息')
        const authParams = await listenForAuthMessage()
        return await validateThirdPartyLogin(authParams)
    } catch (error) {
        logger.warn('从iframe获取认证信息失败:', error)
    }

    throw new Error('未找到有效的登录参数')
}

/**
 * 获取已保存的用户信息
 */
export function getUserInfo() {
    try {
        const userInfoStr = localStorage.getItem(config.auth.userInfoKey)
        if (userInfoStr) {
            return JSON.parse(userInfoStr)
        }
    } catch (error) {
        logger.error('获取用户信息失败:', error)
    }
    return null
}

/**
 * 获取认证token
 */
export function getAuthToken(): string | null {
    return localStorage.getItem(config.auth.accessTokenKey)
}

/**
 * 获取refresh token
 */
export function getRefreshToken(): string | null {
    return localStorage.getItem(config.auth.refreshTokenKey)
}

/**
 * 获取token信息
 */
export function getTokenInfo(): {
    accessToken: string
    refreshToken: string
    expiresIn: number
    loginTime: number
} | null {
    try {
        const tokenInfoStr = localStorage.getItem('tokenInfo')
        if (tokenInfoStr) {
            return JSON.parse(tokenInfoStr)
        }
    } catch (error) {
        logger.error('获取token信息失败:', error)
    }
    return null
}

/**
 * 检查token是否过期
 */
export function isTokenExpired(): boolean {
    const tokenInfo = getTokenInfo()
    if (!tokenInfo) return true

    const now = Date.now()
    const expiredTime = tokenInfo.loginTime + tokenInfo.expiresIn

    // 提前5分钟视为过期，给refresh留出时间
    const bufferTime = 5 * 60 * 1000
    return now >= (expiredTime - bufferTime)
}

/**
 * 刷新token
 */
export async function refreshToken(): Promise<RefreshTokenResponse | null> {
    const refreshTokenValue = getRefreshToken()
    if (!refreshTokenValue) {
        logger.warn('没有refresh token，无法刷新')
        return null
    }

    try {
        logger.log('正在刷新token...')

        const refreshCmd: RefreshTokenCmd = {
            refreshToken: refreshTokenValue
        }

        const response: SingleResponse<RefreshTokenResponse> = await ApiClient.post(
            endpoints.auth.refreshToken,
            refreshCmd,
            { skipAuth: true }
        )

        if (response.success && response.data) {
            const tokenInfo = {
                accessToken: response.data.accessToken,
                refreshToken: response.data.refreshToken,
                expiresIn: response.data.expireTime,
                loginTime: Date.now()
            }

            localStorage.setItem('tokenInfo', JSON.stringify(tokenInfo))
            localStorage.setItem(config.auth.accessTokenKey, response.data.accessToken)
            localStorage.setItem(config.auth.refreshTokenKey, response.data.refreshToken)

            logger.log('token刷新成功')
            return response.data
        }

        throw new Error(response.errMsg || '刷新token失败')

    } catch (error) {
        logger.error('刷新token失败:', error)

        // 开发环境下生成新的JWT token
        if (config.dev.mockApi) {
            const userInfo = getUserInfo()
            const userId = userInfo?.userId || 'refreshed_user'

            const tokenPair = generateTokenPair(userId, userInfo)

            const tokenInfo = {
                accessToken: tokenPair.accessToken,
                refreshToken: tokenPair.refreshToken,
                expiresIn: tokenPair.expiresIn,
                loginTime: Date.now()
            }

            localStorage.setItem('tokenInfo', JSON.stringify(tokenInfo))
            localStorage.setItem(config.auth.accessTokenKey, tokenPair.accessToken)
            localStorage.setItem(config.auth.refreshTokenKey, tokenPair.refreshToken)

            logger.log('开发环境: JWT格式token刷新成功')

            return {
                accessToken: tokenPair.accessToken,
                refreshToken: tokenPair.refreshToken,
                expireTime: Date.now() + tokenPair.expiresIn
            }
        }

        clearAuth()
        return null
    }
}

/**
 * 清除认证信息
 */
export function clearAuth() {
    localStorage.removeItem(config.auth.userInfoKey)
    localStorage.removeItem(config.auth.accessTokenKey)
    localStorage.removeItem(config.auth.refreshTokenKey)
    localStorage.removeItem('tokenInfo')
    logger.log('已清除认证信息')
}

/**
 * 监听来自父窗口的认证参数（iframe场景）
 */
export function listenForAuthMessage(): Promise<ThirdPartyLoginCmd> {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            window.removeEventListener('message', messageHandler)
            reject(new Error('未收到认证信息超时'))
        }, config.ui.loadingTimeout)

        const messageHandler = (event: MessageEvent) => {
            if (event.data && event.data.type === 'THIRD_PARTY_AUTH') {
                clearTimeout(timeoutId)
                window.removeEventListener('message', messageHandler)

                const authData = event.data.params
                if (authData.thirdPartyUserId && authData.signature) {
                    resolve(authData)
                } else {
                    reject(new Error('认证参数不完整'))
                }
            }
        }

        window.addEventListener('message', messageHandler)

        if (window.parent !== window) {
            window.parent.postMessage({type: 'READY_FOR_AUTH'}, '*')
        }
    })
}

/**
 * 获取完整的认证状态
 */
export function getAuthStatus(): {
    isAuthenticated: boolean
    hasValidToken: boolean
    tokenExpired: boolean
    userInfo: any
} {
    const token = getAuthToken()
    const userInfo = getUserInfo()
    const expired = isTokenExpired()

    return {
        isAuthenticated: !!(token && userInfo && !expired),
        hasValidToken: !!token,
        tokenExpired: expired,
        userInfo
    }
}